import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  Check,
  EllipsisVertical,
  Funnel,
  MessageCircleReply,
  PanelLeftOpen,
  Plus,
} from 'lucide-react'

import { Button } from '@/components'
import { ClockRewind } from '@/components/Icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import { useAuthStore } from '@/features/auth'
import { useDocumentStore } from '@/features/document'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { ResponseThread } from '@/schema'
import { cn } from '@/utils'

import { useCommentStore, useTflViewerStore } from '../../store'
import { ECommentStatus } from '../../types'
import { AddCommentDialog } from '../AddCommentDialog'
import DeleteCommentThreadDialog from '../DeleteCommentThreadDialog'

import { useTransferComment } from './hooks/useTransferComment'
import { ActionableDialog } from './ActionableDialog'
import { CommentList } from './CommentList'
import { NotActionableDialog } from './NotActionableDialog'
import { TflFileHistoryDrawer } from './TflFileHistoryDrawer'
import { TransferCommentDialog } from './TransferCommentDialog'

const FilterStatusDropdown = ({
  commentStatus,
  setCommentStatus,
  commentStatusOptions,
}: {
  commentStatus: string | string[]
  setCommentStatus: (val: ECommentStatus) => void
  commentStatusOptions: { value: ECommentStatus; label: string }[]
}) => (
  <Popover>
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <PopoverTrigger asChild>
          <Button
            variant="text"
            className="flex-shrink-0 size-8 p-0 flex items-center justify-center relative group"
            title="Filter statuses"
          >
            <Funnel className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
            {commentStatus !== ECommentStatus.ALL && (
              <div className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-foreground-fg-error-primary" />
            )}
          </Button>
        </PopoverTrigger>
      </TooltipTrigger>
      <TooltipContent>
        <span>Filter statuses</span>
      </TooltipContent>
    </Tooltip>
    <PopoverContent className="sm:max-w-[120px] p-0" side="bottom" align="end">
      <div className="py-1 px-1.5 space-y-0.5">
        {commentStatusOptions.map((option) => {
          const isSelected = commentStatus.includes(option.value)
          return (
            <button
              key={option.value}
              type="button"
              className={cn(
                'p-sm font-medium text-sm cursor-pointer rounded-sm hover:bg-primary-hover flex gap-2 items-center justify-between w-full text-left bg-transparent border-none',
                isSelected ? 'bg-primary-hover' : ''
              )}
              onClick={() => {
                if (isSelected) {
                  setCommentStatus(
                    commentStatus === ECommentStatus.ALL
                      ? option.value
                      : ECommentStatus.ALL
                  )
                } else {
                  setCommentStatus(option.value)
                }
              }}
              aria-label={`${isSelected ? 'Deselect' : 'Select'} ${option.label}`}
              aria-pressed={isSelected}
            >
              <span>{option.label}</span>
              {isSelected && (
                <Check
                  size={16}
                  className="text-foreground-fg-brand-primary-600"
                />
              )}
            </button>
          )
        })}
      </div>
    </PopoverContent>
  </Popover>
)

const SidebarTransferMenu = ({
  isShow,
  transferDialogOpen,
  setTransferDialogOpen,
  shouldShowTransferButton,
  setHistoryDrawerOpen,
  t,
}: {
  isShow: boolean
  transferDialogOpen: boolean
  setTransferDialogOpen: (val: boolean) => void
  shouldShowTransferButton: boolean
  setHistoryDrawerOpen: (val: boolean) => void
  t: ReturnType<typeof useTranslation>['t']
}) => {
  return (
    <>
      {isShow && (
        <TransferCommentDialog
          open={transferDialogOpen}
          onOpenChange={setTransferDialogOpen}
        />
      )}

      {shouldShowTransferButton ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="text"
              className="flex-shrink-0 size-8 p-0 hover:bg-gray-50 focus-visible:ring-0 group"
            >
              <EllipsisVertical className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[180px]">
            <DropdownMenuItem
              className="cursor-pointer flex items-center gap-2"
              onClick={() => setTransferDialogOpen(true)}
            >
              <MessageCircleReply className="size-4" />
              {t('tflViewer.commentSidebar.transferComments')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer flex items-center gap-2"
              onClick={() => setHistoryDrawerOpen(true)}
            >
              <ClockRewind className="size-4" />
              {t('history.title', 'History')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="text"
                className="flex-shrink-0 size-8 p-0 hover:bg-gray-50 focus-visible:ring-0 group"
                onClick={() => setHistoryDrawerOpen(true)}
              >
                <ClockRewind className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {t('history.title', 'History')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </>
  )
}

const SidebarHeaderActions = ({
  isRightSidebarCollapsed,
  isShowAddCommentButton,
  setCommentDialogOpen,
  commentStatus,
  setCommentStatus,
  commentStatusOptions,
  isShowTransferCommentButton,
  transferDialogOpen,
  setTransferDialogOpen,
  shouldShowTransferButton,
  setHistoryDrawerOpen,
  setRightSidebarCollapsed,
  t,
}: {
  isRightSidebarCollapsed: boolean
  isShowAddCommentButton: boolean
  setCommentDialogOpen: (val: boolean) => void
  commentStatus: string | string[]
  setCommentStatus: (val: ECommentStatus) => void
  commentStatusOptions: { value: ECommentStatus; label: string }[]
  isShowTransferCommentButton: boolean
  transferDialogOpen: boolean
  setTransferDialogOpen: (val: boolean) => void
  shouldShowTransferButton: boolean
  setHistoryDrawerOpen: (val: boolean) => void
  setRightSidebarCollapsed: (val: boolean) => void
  t: ReturnType<typeof useTranslation>['t']
}) => {
  return (
    <div className="flex items-center">
      <div className="flex-1 flex items-center gap-1">
        {isShowAddCommentButton && (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="text"
                className="flex-shrink-0 size-8 p-0 flex items-center justify-center relative group"
                onClick={() => setCommentDialogOpen(true)}
              >
                <Plus className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <span>Add comment</span>
            </TooltipContent>
          </Tooltip>
        )}

        {!isRightSidebarCollapsed && (
          <>
            <FilterStatusDropdown
              commentStatus={commentStatus}
              setCommentStatus={setCommentStatus}
              commentStatusOptions={commentStatusOptions}
            />

            <SidebarTransferMenu
              isShow={isShowTransferCommentButton}
              transferDialogOpen={transferDialogOpen}
              setTransferDialogOpen={setTransferDialogOpen}
              shouldShowTransferButton={shouldShowTransferButton}
              setHistoryDrawerOpen={setHistoryDrawerOpen}
              t={t}
            />

            <div className="h-4 border-r border-secondary mr-1" />
          </>
        )}
      </div>

      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Button
            variant="text"
            className="flex-shrink-0 size-8 p-0 hover:bg-gray-50 group"
            onClick={() => setRightSidebarCollapsed(!isRightSidebarCollapsed)}
            title={
              isRightSidebarCollapsed
                ? ''
                : t('tflViewer.commentSidebar.collapseSidebar')
            }
            aria-label={
              isRightSidebarCollapsed
                ? ''
                : t('tflViewer.commentSidebar.collapseSidebar')
            }
          >
            {!isRightSidebarCollapsed && (
              <PanelLeftOpen className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <span>{isRightSidebarCollapsed ? '' : 'Collapse'}</span>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

interface CommentSidebarProps {
  isVisible?: boolean
  targetCommentId?: string | null
  onTargetCommentFound?: (comment: ResponseThread) => void
  onTargetCommentNotFound?: () => void
}

export const CommentSidebar: React.FC<CommentSidebarProps> = ({
  isVisible = true,
  targetCommentId,
  onTargetCommentFound,
  onTargetCommentNotFound,
}) => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const getListDLViewerHasAccess = useAuthStore(
    (state) => state.getListDLViewerHasAccess
  )
  const getListDLReviewerHasAccess = useAuthStore(
    (state) => state.getListDLReviewerHasAccess
  )
  const listDlReviewerHasAccess = getListDLReviewerHasAccess() as string[]
  const listDLViewerHasAccess = getListDLViewerHasAccess() as string[]

  const isReviewer = listDlReviewerHasAccess.includes(id as string)
  const isViewer = listDLViewerHasAccess.includes(id as string)

  const { isLg: isLargeBreakpoint } = useBreakpoint()

  const { commentStatus, setCommentStatus } = useCommentStore()

  const { activeDocumentId, getIsLatestVersion } = useDocumentStore()
  const isLatestVersion = activeDocumentId
    ? getIsLatestVersion(activeDocumentId)
    : true

  const {
    metadataByDocId,
    commentDialogOpen,
    transferDialogOpen,
    historyDrawerOpen,
    isRightSidebarCollapsed,
    setCommentDialogOpen,
    setTransferDialogOpen,
    setHistoryDrawerOpen,
    setRightSidebarCollapsed,
  } = useTflViewerStore()

  // Get version_id from the active document metadata
  const documentVersionId = useMemo(
    () =>
      activeDocumentId
        ? metadataByDocId[activeDocumentId]?.version_id
        : undefined,
    [activeDocumentId, metadataByDocId]
  )

  const commentStatusOptions = useMemo(
    () => [
      {
        value: ECommentStatus.OPEN,
        label: t('tflViewer.commentSidebar.status.open'),
      },
      {
        value: ECommentStatus.RESOLVED,
        label: t('tflViewer.commentSidebar.status.resolved'),
      },
    ],
    [t]
  )
  const isShowAddCommentButton =
    !isRightSidebarCollapsed && isLatestVersion && !isViewer
  const isShowTransferCommentButton =
    !isRightSidebarCollapsed && isLatestVersion && !isViewer && !isReviewer

  const { isAllCommentTransferred, listVersion } = useTransferComment()
  const shouldShowTransferButton =
    isShowTransferCommentButton &&
    !isAllCommentTransferred &&
    listVersion.length > 1

  return (
    <div
      className={cn(
        'h-full flex flex-col bg-white transition-all duration-300',
        'border border-r-0 border-y-0 border-solid border-secondary',
        isRightSidebarCollapsed
          ? 'w-0 flex-shrink-0'
          : 'w-sidebar flex-[0_0_280px]',
        isVisible ? '' : '!w-0 flex-[0_0_0]',
        isLargeBreakpoint ? '' : 'absolute top-0 right-0 bottom-0'
      )}
    >
      <div className="flex flex-col h-full w-sidebar">
        <div className="flex items-center p-4 border border-x-0 border-t-0 border-solid border-secondary">
          <div className="flex justify-between items-center flex-1">
            {isRightSidebarCollapsed ? null : (
              <div className="text-lg font-semibold text-gray-900">
                {t('tflViewer.commentSidebar.title')}
              </div>
            )}

            <SidebarHeaderActions
              isRightSidebarCollapsed={isRightSidebarCollapsed}
              isShowAddCommentButton={isShowAddCommentButton}
              setCommentDialogOpen={setCommentDialogOpen}
              commentStatus={commentStatus}
              setCommentStatus={setCommentStatus}
              commentStatusOptions={commentStatusOptions}
              isShowTransferCommentButton={isShowTransferCommentButton}
              transferDialogOpen={transferDialogOpen}
              setTransferDialogOpen={setTransferDialogOpen}
              shouldShowTransferButton={shouldShowTransferButton}
              setHistoryDrawerOpen={setHistoryDrawerOpen}
              setRightSidebarCollapsed={setRightSidebarCollapsed}
              t={t}
            />
          </div>
        </div>
        {isRightSidebarCollapsed ? null : (
          <div className="flex flex-col flex-1 min-h-0 bg-secondary-alt">
            <CommentList
              documentVersionId={documentVersionId}
              targetCommentId={targetCommentId}
              onTargetCommentFound={onTargetCommentFound}
              onTargetCommentNotFound={onTargetCommentNotFound}
            />
          </div>
        )}
      </div>

      {activeDocumentId && documentVersionId && (
        <>
          <AddCommentDialog
            open={commentDialogOpen}
            onOpenChange={setCommentDialogOpen}
            documentVersionId={documentVersionId}
          />
          <TflFileHistoryDrawer
            open={historyDrawerOpen}
            onOpenChange={setHistoryDrawerOpen}
            documentId={activeDocumentId}
          />
        </>
      )}
      <ActionableDialog />
      <NotActionableDialog />
      <DeleteCommentThreadDialog />
    </div>
  )
}
